import { Component } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmComponent } from '../../components/confirm/confirm.component';
import { MessagesService } from '../../services/messages.service';
import { UserService } from '../../services/user.service';
import { AlertsService } from '../../services/alerts.service';
import { Subscription } from 'rxjs';

/**
 * Component for managing a user's system messages.
 */
@Component({
	selector: 'user-messages',
	templateUrl: './messages.component.html',
	styleUrl: './messages.component.css'
})
export class MessagesComponent implements OnInit, OnDestroy {
    
    private userServiceSubscription: Subscription = new Subscription();
	LOCALIZATION: any;
	messages: any[] = [];
	totalMessageCount: number = 0;
	selectedAllMessages: boolean = false;
	selectedMessageIds: any[] = [];
	messagesPagination = {
		page: 0,
		size: 10,
		first: true,
		last: true
	};

	constructor(
		private messagesService: MessagesService,
		private alertsService: AlertsService,
		private userService: UserService,
		private modalService: NgbModal
	) { 
		this.LOCALIZATION = this.userService.LOCALIZATION;
	}

	ngOnInit(): void {
		this.getMessages();
		this.getTotalMessageCount();
		this.userServiceSubscription = this.userService.broadcastEvent.subscribe((eventId: string) => {
		switch (eventId) {
			case 'localizationLoaded':
				this.LOCALIZATION = this.userService.LOCALIZATION;
				break;
		}
		});
	}
	ngOnDestroy() {
		if (this.userServiceSubscription) {
			this.userServiceSubscription.unsubscribe();
		}
	}
	getTotalMessageCount(): void {
		this.messagesService.getUserMessagesCount().then(
			(count: any) => {
				this.totalMessageCount = count;
			},
			(err: any) => {
				console.error("Failed to get user message count", err);
			}
		);
	}

	getMessages(): void {
		this.selectedMessageIds = [];

		this.messagesService.getUserMessages(this.messagesPagination.page, this.messagesPagination.size).then(
			(response: any) => {
				this.messagesPagination.first = response.first;
				this.messagesPagination.last = response.last;

				for (let  i = 0; i < response.content.length; i++) {
					let  msg = response.content[i];
					let  created = new Date(msg.created);
					msg.created = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');
					if (msg.message) {
						try {
							msg.messageObj = JSON.parse(msg.message);
						} catch (err) {
							console.error("Failed to parse message", err);
						}
					}
				}

				this.messages = response.content;
			},
			(err: any) => {
				console.error("Failed to get user messages", err);
			}
		);
	}

	toggleMessage(id: number): void {
		let  index = this.selectedMessageIds.indexOf(id);
		if (index > -1) {
			this.selectedMessageIds.splice(index, 1);
		} else {
			this.selectedMessageIds.push(id);
		}

		if (this.selectedMessageIds.length == this.messages.length) {
			this.selectedAllMessages = true;
		} else {
			this.selectedAllMessages = false;
		}
	}

	deleteAllMessages(): void {
		const modalRef = this.modalService.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'deleteAllMessages',
			title: this.LOCALIZATION.deleteMessagesHeader,
			message: this.LOCALIZATION.deleteMessagesMessage,
			type: 'danger'
		};

		modalRef.result.then(
			(payload) => {
				if (payload.result?.answer === 'ok') {
					this.messagesService.deleteAllUserMessages().then(
						(response: any) => {
							this.alertsService.success({
								message: this.LOCALIZATION.deleteMessagesSuccess,
							});
							this.refresh();
						},
						(err: any) => {
							console.error("Failed to delete messages", err);
							this.alertsService.error({
								message: this.LOCALIZATION.deleteMessagesError
							});
						}
					);
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	deleteMessages(): void {
		if (this.selectedMessageIds.length > 0) {
			this.messagesService.deleteUserMessage(this.selectedMessageIds).then(
				(response: any) => {
					this.alertsService.success({
						title: "User Messages Deleted",
						message: `Successfully deleted ${response.success} messages.`
					});
					this.getMessages();
				},
				(err: any) => {
					console.error("Failed to delete messages", err);
					this.alertsService.error({
						title: "Message Deletion Failed",
						delay: 5000
					});
				}
			);
		}
	}

	openMessage(index: number): void {
		let  message = this.messages[index];
		message.open = !message.open;
		message.read = true;
		this.messagesService.readUserMessage(message.id).then(
			(response: any) => {
				this.messagesService.triggerMessagesReload();
				this.messagesService.triggerMessagesReloaded();
			},
			(err: any) => {
				console.error("Failed to open message", err);
			}
		);
	}

	readAllMessages(): void {
		const modalRef = this.modalService.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'readAllMessages',
			title: this.LOCALIZATION.readMessagesHeader,
			message: this.LOCALIZATION.readMessagesMessage,
			type: 'primary'
		};

		modalRef.result.then(
			(payload) => {
				if (payload.result?.answer === 'ok') {
					this.messagesService.readAllUserMessages().then(
						(response: any) => {
							this.refresh();
						},
						(err: any) => {
							console.error("Failed to read messages.", err);
							this.alertsService.error({
								message: this.LOCALIZATION.readMessagesError,
							});
						}
					);
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	nextPage(): void {
		if (!this.messagesPagination.last) {
			this.messagesPagination.page++;
			this.getMessages();
		}
	}

	prevPage(): void {
		if (!this.messagesPagination.first) {
			this.messagesPagination.page--;
			this.getMessages();
		}
	}

	refresh(): void {
		this.messagesPagination.page = 0;
		this.getMessages();
		this.messagesService.triggerMessagesReload();
		this.messagesService.triggerMessagesReloaded();  
	}
}
